import { Appearance, ExpressPaymentType, StripeElementsOptionsMode, StripeExpressCheckoutElementOptions, StripePaymentElementOptions } from "@stripe/stripe-js";
import { ApplePayOption, ApplePayRecurringPaymentRequest, ApplePayRecurringPaymentRequestIntervalUnit } from "@stripe/stripe-js/dist/stripe-js/elements/apple-pay";
import { Quote } from "@/shared/types/Quote.interface";
import { PublicConfig } from "@/shared/PublicConfig";

export interface StripePaymentElementError {
    stripe: boolean;
    header?: string;
    message?: string;
    description?: string;
}

// Todo: Does PaymentMethod exist in Stripe types? StripePaymentElementChangeEvent has 'type' as string, but references PaymentMethod in the comments
export type StripePaymentMethod = "card" | ExpressPaymentType | undefined;

export default class StripeUtils {
    // Style the Stripe Elements with Appearance API
    // https://docs.stripe.com/elements/appearance-api
    static readonly themeOptions: Appearance = {
        theme: "stripe",
        variables: {
            borderRadius: "0.375rem",
            fontFamily: "Poppins, sans-serif",
            fontSmooth: "auto",
            gridRowSpacing: "1.25rem"
        },
        rules: {
            ".Label": {
                fontSize: "14px",
                fontWeight: "500",
                color: "#003E52"
            },
            ".Input": {
                paddingLeft: "1rem",
                border: "none",
                boxShadow: "0 0 0 1px #d1d5db"
            },
            ".Input:focus": {
                borderColor: "none",
                boxShadow: "0 0 0 4px rgba(0, 124, 131, 0.4)"
            },
            ".Error": {
                fontSize: ".75rem"
            }
        }
    };

    static readonly roundedThemeOptions: Appearance = {
        theme: "stripe",
        variables: {
            borderRadius: "1.75rem",
            fontFamily: "Poppins, sans-serif",
            fontSmooth: "auto",
            gridRowSpacing: "1.25rem"
        },
        rules: {
            ".Label": {
                fontSize: "14px",
                fontWeight: "500",
                color: "#003E52"
            },
            ".Input": {
                paddingLeft: "1rem",
                border: "none",
                boxShadow: "0 0 0 1px #d1d5db",
                borderRadius: ".75rem"
            },
            ".Input:focus": {
                borderColor: "none",
                boxShadow: "0 0 0 4px rgba(0, 124, 131, 0.4)"
            },
            ".Error": {
                fontSize: ".75rem"
            }
        }
    };

    // https://docs.stripe.com/js/elements_object/create
    static getElementsOptions(amount: number): StripeElementsOptionsMode {
        const parsedAmount = parseFloat((amount * 100).toFixed(0));

        const elementsOptions: StripeElementsOptionsMode = {
            mode: "payment",
            amount: parsedAmount,
            currency: "usd",
            paymentMethodCreation: "manual",
            setupFutureUsage: "off_session",
            appearance: StripeUtils.roundedThemeOptions,
            fonts: [{ cssSrc: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500" }]
        };

        if (PublicConfig.PTZ_US.STRIPE_ON_BEHALF_OF_ENABLED && PublicConfig.PTZ_US.STRIPE_CONNECTED_ACCOUNT_ID) {
            elementsOptions.onBehalfOf = PublicConfig.PTZ_US.STRIPE_CONNECTED_ACCOUNT_ID;
        }

        return elementsOptions;
    }

    static readonly expressPaymentOptions: StripeExpressCheckoutElementOptions = {
        buttonType: {
            googlePay: "checkout",
            applePay: "check-out"
        },
        paymentMethods: {
            link: "never"
        },
        layout: {
            // Prevents 'See more' button from appearing
            overflow: "never"
        }
    };

    static paymentElementOptions: StripePaymentElementOptions = {
        fields: {
            billingDetails: "never" // hides the address fields
        },
        // hides the card terms, ex: "By providing your card information, you allow Spot to charge your card..."
        terms: {
            card: "never",
            googlePay: "never",
            applePay: "never"
        },
        wallets: {
            googlePay: "never",
            applePay: "never"
        },
        layout: {
            type: "auto"
        }
    };

    /**
     * Converts a Quote object into an ApplePayRecurringPaymentRequest for Stripe Express Checkout.
     * @param quote The quote object (potentially partial).
     * @returns An ApplePayRecurringPaymentRequest object.
     * @throws Error if the quote does not contain necessary information (policies, prices, frequency).
     */
    static convertQuoteToApplePayRecurringRequest(quote: Quote): ApplePayOption {
        let totalAmount = 0;
        if (quote.policies && quote.policies.length > 0) {
            totalAmount = quote.policies.reduce((sum, policy) => {
                // Ensure policy, finalPrice, and value exist before adding
                const priceValue = policy?.finalPrice?.value;
                return sum + (typeof priceValue === "number" ? priceValue : 0);
            }, 0);
        }

        // Add transaction fee if present
        const transactionFeeValue = quote.transactionFee?.value;
        if (typeof transactionFeeValue === "number") {
            totalAmount += transactionFeeValue;
        }

        if (totalAmount <= 0) {
            throw new Error("Cannot create Apple Pay recurring request: Invalid total amount calculated from quote policies.");
        }

        const totalAmountCents = Math.round(totalAmount * 100); // Convert to cents and round

        const frequency = quote.billingInfo?.frequency;
        if (!frequency) {
            throw new Error("Cannot create Apple Pay recurring request: Billing frequency is missing.");
        }

        const intervalUnit: ApplePayRecurringPaymentRequestIntervalUnit = frequency === "yearly" ? "year" : "month";
        const intervalCount = 1;

        const billingLabel = intervalUnit === "month" ? "Monthly premium" : "Annual premium";

        const recurringPaymentRequest: ApplePayRecurringPaymentRequest = {
            paymentDescription: "Spot Pet Insurance",
            managementURL: PublicConfig.MEMBER_LOGIN_URL,
            regularBilling: {
                amount: totalAmountCents,
                label: billingLabel,
                recurringPaymentIntervalUnit: intervalUnit,
                recurringPaymentIntervalCount: intervalCount
            }
        };

        return {
            recurringPaymentRequest: recurringPaymentRequest,
            deferredPaymentRequest: null,
            automaticReloadPaymentRequest: null
        };
    }
}
